import {
    DraasApiPlanRecoverySummary,
    DraasApiRecoveryPlanExtended,
    DraasApiTargetSiteDetails,
} from '@pure/paas-api-gateway-client-ts';
import moment from 'moment';
import { DisasterRecoveryPlan, DisasterRecoveryJobExecutionInfo, DisasterRecoveryPlanType } from '@pure1/data';
import { DisasterRecoverySiteStatus, DisasterRecoverySiteType } from './disaster-recovery-site';

export class DisasterRecoveryPlanExtended {
    recoveryPlan: DisasterRecoveryPlan;
    totalVms: number;
    recoverySummary: DisasterRecoveryPlanSummary;
    targetSiteDetails: DisasterRecoveryTargetSiteDetails;
    lastJobExecutionPlanInfo: DisasterRecoveryJobExecutionInfo;

    constructor(json: DraasApiRecoveryPlanExtended) {
        this.recoveryPlan = new DisasterRecoveryPlan(json.recovery_plan);
        this.totalVms = json.total_vms;
        this.recoverySummary = new DisasterRecoveryPlanSummary(json.recovery_summary);
        this.targetSiteDetails = json.target_site_details
            ? new DisasterRecoveryTargetSiteDetails(json.target_site_details)
            : null;
        this.lastJobExecutionPlanInfo = json.last_job_execution_plan_info
            ? new DisasterRecoveryJobExecutionInfo(json.last_job_execution_plan_info)
            : null;
    }

    getComposedRecoveryState(): DisasterRecoveryComposedPlanState {
        switch (this.recoveryPlan.planState) {
            case 'NOT_IN_FAILOVER_MODE':
                return 'HEALTHY';
            case 'TRANSITIONING_TO_FAILOVER_MODE':
                if (this.recoveryPlan.planType === 'PRODUCTION') {
                    return 'PROD_FAILOVER_IN_PROGRESS';
                }
                return 'TEST_FAILOVER_IN_PROGRESS';
            case 'SUCCESSFUL_FAILOVER_MODE':
                switch (this.lastJobExecutionPlanInfo?.purpose) {
                    case 'FAILOVER_PROD':
                        return this.determineProdFailoverState(this.lastJobExecutionPlanInfo?.status);
                    case 'FAILOVER_TEST':
                        return this.determineTestFailoverState(this.lastJobExecutionPlanInfo?.status);
                    // This is for backwards compatibility with old plans and old executions
                    case 'FAILOVER':
                        return this.determineFailoverState(
                            this.lastJobExecutionPlanInfo?.status,
                            this.recoveryPlan.planType,
                        );
                    default:
                        return 'UNKNOWN';
                }
            case 'TEARING_DOWN_FAILOVER_MODE':
                return 'TEARDOWN_IN_PROGRESS';
            case 'TRANSITIONING_TO_FAILBACK_MODE':
                return 'FAILBACK_IN_PROGRESS';
            case 'SUCCESSFUL_FAILBACK_MODE':
                // Yes, after failback we should be back to healthy
                return 'HEALTHY';
            case 'FAILBACK_SYNC_IN_PROGRESS':
                return 'FAILBACK_SYNC_IN_PROGRESS';
            case 'FAILBACK_SYNC_COMPLETE':
                return 'FAILBACK_SYNC_MODE';
            case 'FAILBACK_SYNC_FAILED':
                return 'FAILBACK_SYNC_FAILED';
            case 'TRANSITIONING_TO_FAILBACK_CUTOVER_MODE':
                return 'FAILBACK_CUTOVER_IN_PROGRESS';
            case 'FAILBACK_CUTOVER_MODE':
                return 'FAILBACK_CUTOVER_MODE';
            case 'FAILBACK_CUTOVER_FAILED':
                return 'FAILBACK_CUTOVER_FAILED';
            case 'FAILBACK_PROMOTE_IN_PROGRESS':
                return 'FAILBACK_PROMOTE_IN_PROGRESS';
            case 'FAILBACK_PROMOTE_FAILED':
                return 'FAILBACK_PROMOTE_FAILED';
            case 'CRITICAL_FAILURE':
                return 'CRITICAL_FAILURE';
            default:
                return 'UNKNOWN';
        }
    }

    private determineProdFailoverState(jobExecutionPlanInfoState: string): DisasterRecoveryComposedPlanState {
        switch (jobExecutionPlanInfoState) {
            case 'FINISHED_PARTIALLY_SUCCESSFULLY':
                return 'PARTIALLY_SUCCESSFUL_PROD_FAILOVER_MODE';
            case 'FINISHED_SUCCESSFULLY':
                return 'PROD_FAILOVER_MODE';
            case 'FAILED':
                return 'PROD_FAILOVER_FAILED';
            default:
                return 'UNKNOWN';
        }
    }

    private determineTestFailoverState(jobExecutionPlanInfoState: string): DisasterRecoveryComposedPlanState {
        switch (jobExecutionPlanInfoState) {
            case 'FINISHED_PARTIALLY_SUCCESSFULLY':
                return 'PARTIALLY_SUCCESSFUL_TEST_FAILOVER_MODE';
            case 'FINISHED_SUCCESSFULLY':
                return 'TEST_FAILOVER_MODE';
            case 'FAILED':
                return 'TEST_FAILOVER_FAILED';
            default:
                return 'UNKNOWN';
        }
    }

    private determineFailoverState(
        jobExecutionPlanInfoState: string,
        planType: DisasterRecoveryPlanType,
    ): DisasterRecoveryComposedPlanState {
        switch (jobExecutionPlanInfoState) {
            case 'FINISHED_PARTIALLY_SUCCESSFULLY':
                if (planType === 'PRODUCTION') {
                    return 'PARTIALLY_SUCCESSFUL_PROD_FAILOVER_MODE';
                }
                return 'PARTIALLY_SUCCESSFUL_TEST_FAILOVER_MODE';
            case 'FINISHED_SUCCESSFULLY':
                if (planType === 'PRODUCTION') {
                    return 'PROD_FAILOVER_MODE';
                }
                return 'TEST_FAILOVER_MODE';
            case 'FAILED':
                if (planType === 'PRODUCTION') {
                    return 'PROD_FAILOVER_FAILED';
                }
                return 'TEST_FAILOVER_FAILED';
            default:
                return 'UNKNOWN';
        }
    }
}

export type DisasterRecoveryComposedPlanState =
    | 'HEALTHY'
    | 'PROD_FAILOVER_MODE'
    | 'TEST_FAILOVER_MODE'
    | 'PARTIALLY_SUCCESSFUL_PROD_FAILOVER_MODE'
    | 'PARTIALLY_SUCCESSFUL_TEST_FAILOVER_MODE'
    | 'FAILBACK_SYNC_MODE'
    | 'FAILBACK_MODE'
    | 'FAILBACK_CUTOVER_MODE'
    | 'PROD_FAILOVER_IN_PROGRESS'
    | 'TEST_FAILOVER_IN_PROGRESS'
    | 'TEARDOWN_IN_PROGRESS'
    | 'FAILBACK_IN_PROGRESS'
    | 'PROD_FAILOVER_FAILED'
    | 'TEST_FAILOVER_FAILED'
    | 'FAILBACK_SYNC_IN_PROGRESS'
    | 'FAILBACK_CUTOVER_IN_PROGRESS'
    | 'FAILBACK_PROMOTE_IN_PROGRESS'
    | 'FAILBACK_SYNC_FAILED'
    | 'FAILBACK_CUTOVER_FAILED'
    | 'FAILBACK_PROMOTE_FAILED'
    | 'CRITICAL_FAILURE'
    | 'UNKNOWN';

export class DisasterRecoveryPlanSummary {
    success: number;
    failed: number;
    neverRun: number;
    lastRto?: moment.Duration;

    constructor(json: DraasApiPlanRecoverySummary) {
        this.success = json.success;
        this.failed = json.failed;
        this.neverRun = json.never_run;
        this.lastRto = json.last_rto ? moment.duration(json.last_rto) : null;
    }
}

export class DisasterRecoveryTargetSiteDetails {
    id: string;
    name: string;
    type: DisasterRecoverySiteType;
    status: DisasterRecoverySiteStatus;
    version: number;

    constructor(json: DraasApiTargetSiteDetails) {
        this.id = json.id;
        this.name = json.name;
        this.type = json.type;
        this.status = json.status;
        this.version = json.version;
    }
}
